import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PermissionCode } from "@/constants/permissions";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { useRoles } from "@/hooks/useRoles";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { TrucksFilter } from "@/components/common/section-components/filters/TrucksFilter";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  TruckListEntity,
  trucksFilterSchema,
  TrucksListResponse,
} from "./schema/trucks.schema";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  clearFiltersHandler,
  submitFiltersHandler,
  fetchTrucksHandler,
  handleDeleteTrucksHandler,
} from "./schema/handler-functions";
import { GetTrucksListProps } from "@/services/trucks.service";
import { isEmpty } from "@/utils/query-string";
import { NoData } from "@/components/common/section-components/NoData";
import { StandardTable } from "@/components/table/StandardTable";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { TrucksActions } from "@/components/common/section-components/action/TrucksActions";
import { trucksHeaderElements } from "./schema/header-elements";

export const TrucksPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, [isActive]);
  const { userPermissions, fetchPermissions } = useRoles();
  const navigate = useNavigate();

  const { control, setValue, getValues, handleSubmit } = useForm({
    resolver: zodResolver(trucksFilterSchema),
  });

  const [currentFilters, setCurrentFilters] = useState<
    GetTrucksListProps | undefined
  >();

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) => p.codigo === PermissionCode.TRUCKS && p.tipoPermiso === "W",
    ),
  );

  const handleAddTruck = () => {
    navigate(AppRoutes.TRUCKS_CREATION);
  };

  const [trucks, setTrucks] = useState<TrucksListResponse | null>(null);

  const [loadingTrucks, setLoadingTrucks] = useState<boolean>(false);
  const [loadingDeleteTrucks, setLoadingDeleteTrucks] =
    useState<boolean>(false);
  const [page, setPage] = useState(1);

  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;

  const [openFilters, setOpenFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const fetchTrucks = async (filters?: GetTrucksListProps, showLoad = true) => {
    await fetchTrucksHandler({
      setLoadingTrucks,
      filters,
      showLoad,
      setTrucks,
      navigate,
      fetchPermissions,
    });
  };
  const submitFilters = async () => {
    submitFiltersHandler({
      event: getValues(),
      setFiltersApplied,
      setOpenFilters,
      fetchTrucks,
      setCurrentFilters,
    });
  };
  const clearFilters = async () => {
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
      setOpenFilters,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchTrucks();
    }
  }, [isActive]);
  const values = useWatch({
    control,
  });
  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  const handlePageChange = (page: number) => {
    setPage(page);
    fetchTrucks({ ...currentFilters, "P.Pagina": page });
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.TRUCKS_EDIT.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: any) => (
    <TrucksActions handleDetail={handleDetail} item={item} />
  );

  const { open: openConfirm, setOpen: setOpenConfirmModal } = useDisclosure();

  const handleDelete = async () => {
    handleDeleteTrucksHandler({
      selection,
      setLoadingDeleteTrucks,
      setOpenConfirm: setOpenConfirmModal,
      fetchTrucks,
      setSelection,
    });
  };

  return (
    <Flex flexDir="column" paddingTop="20px" data-testid="trucks-page">
      <SectionHeader
        title={"Maestro de camiones"}
        addButtonLabel="Agregar"
        deleteButtonLabel="Eliminar"
        handleAdd={handleAddTruck}
        handleDelete={() => setOpenConfirmModal(true)}
        disableDelete={!selection?.length}
        loading={loadingTrucks}
        showButtons={hasWriteAccess}
      />

      <TrucksFilter
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        disableFilters={loadingTrucks}
        loadingFilters={loadingTrucks}
        filtersApplied={filtersApplied}
        disableClear={loadingTrucks}
        disableApply={filtersEmpty}
      />

      <Flex flexDir="column">
        {Boolean(!loadingTrucks && !trucks?.listado?.length) && <NoData />}
        {Boolean(!loadingTrucks && trucks?.listado?.length) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              items={
                trucks?.listado?.map((el: TruckListEntity) => ({
                  ...el,
                  estado: el?.activo ? "Activo" : "Inactivo",
                  modulos: el?.modulos?.join(", "),
                })) ?? []
              }
              headers={trucksHeaderElements}
              actionElements={getActionElements}
            />
            {trucks?.totalPages && trucks?.totalPages > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={trucks?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>

      <Suspense>
        <ConfirmModalDynamic
          openConfirm={openConfirm}
          setOpenConfirm={setOpenConfirmModal}
          title={`Eliminar camiones`}
          subtitle={`¿Desea eliminar los camiones (${selection?.length}) seleccionados? No podrá deshacer esta acción después.`}
          loadingAccept={loadingDeleteTrucks}
          handleAccept={handleDelete}
        />
      </Suspense>
    </Flex>
  );
};

const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);
